import {
  Box,
  Chip,
  Divider,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {FixedSizeList as Lists} from 'react-window';
import {
  useCallback,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import type {IChildPartner} from '../../../../dto/Company.dto';
import {getInheritedChildren} from './districtPartners';
import {tnnDistrictPartnerConfig} from '../../../../config/messageConfig';
import {
  formatTnnCurrency,
  getPartnerShare,
  getPartnerName,
  getTnnPartnerAmount,
  ITnnPartnerAmountPartner,
  sumPartnerAmount,
  sumPartnerShare,
  TNN_PARTNER_AMOUNT_FIELDS,
  TnnPartnerAmountOptions,
} from './tnnPartnerUtils';

const partnerAmountGridColumns = (hasRunsMetrics: boolean) => ({
  xs: hasRunsMetrics
    ? 'minmax(140px, 1.5fr) 80px 80px 95px 85px 85px'
    : 'minmax(120px, 1fr) 90px 90px',
  sm: hasRunsMetrics
    ? 'minmax(160px, 1.5fr) 90px 90px 105px 95px 95px'
    : 'minmax(140px, 1fr) 100px 100px',
});

const PartnerAmountGrid = ({
  children,
  variant = 'header',
  hasRunsMetrics = false,
}: {
  children: ReactNode;
  variant?: 'header' | 'row' | 'total';
  hasRunsMetrics?: boolean;
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: partnerAmountGridColumns(hasRunsMetrics),
      gap: 1.5,
      alignItems: 'center',
      px: 3,
      ...(variant === 'row' ? {minHeight: 48, py: 0.5} : {py: 1.5}),
      ...(variant === 'total'
        ? {
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'customBgColors.blue_variant6',
            fontWeight: 600,
          }
        : {}),
    }}>
    {children}
  </Box>
);

/**
 * A district row is followed by its inherited child partners when expanded, so
 * the whole table stays one fixed-height virtual list.
 */
export type PartnerAmountRowItem =
  | {kind: 'partner'; partner: ITnnPartnerAmountPartner}
  | {
      kind: 'child';
      child: IChildPartner;
      districtId: number | string;
      isLastChild: boolean;
    };

type PartnerAmountRowData = {
  rows: PartnerAmountRowItem[];
  expandedDistrictIds: (number | string)[];
  onToggleDistrict: (districtId: number | string) => void;
  editable: boolean;
  onPriceChange?: (companyId: number, value: string) => void;
  emptyValue: string;
  options: TnnPartnerAmountOptions;
  calculateShareWhenMissing: boolean;
  currencyMinimumFractionDigits: number;
  numberOfRuns?: number;
};

type PartnerAmountRowProps = {
  index: number;
  style: CSSProperties;
  data: PartnerAmountRowData;
};

const PartnerAmountRow = ({index, style, data}: PartnerAmountRowProps) => {
  const row = data.rows[index];
  if (!row) return null;

  if (row.kind === 'child') {
    return (
      <Box style={style} sx={{bgcolor: 'rgba(255, 255, 255, 0.03)'}}>
        <PartnerAmountGrid variant='row'>
          <Stack
            direction='row'
            alignItems='center'
            spacing={1}
            sx={{minWidth: 0, pl: 2}}>
            <Box
              sx={{
                width: 10,
                height: 10,
                flexShrink: 0,
                mt: -0.75,
                borderLeft: '1px solid',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            />
            <Typography
              noWrap
              title={row.child.name}
              sx={{minWidth: 0, fontSize: 13, color: 'text.secondary'}}>
              {row.child.name}
            </Typography>
            <Chip
              label={tnnDistrictPartnerConfig.inheritedChild}
              size='small'
              sx={{
                flexShrink: 0,
                height: 18,
                fontSize: 10,
                color: 'text.secondary',
              }}
            />
          </Stack>
          <Typography sx={{fontSize: 13, color: 'text.disabled'}}>—</Typography>
          <Typography sx={{fontSize: 13, color: 'text.disabled'}}>—</Typography>
        </PartnerAmountGrid>
        {row.isLastChild ? <Divider sx={{borderColor: 'divider'}} /> : null}
      </Box>
    );
  }

  const {partner} = row;
  const name = getPartnerName(partner);
  const children = getInheritedChildren(partner);
  const isExpanded = data.expandedDistrictIds.includes(partner.id);
  const price = getTnnPartnerAmount(
    partner,
    TNN_PARTNER_AMOUNT_FIELDS.price,
    data.options,
  );
  const share = data.calculateShareWhenMissing
    ? getPartnerShare(partner, undefined, data.options)
    : getTnnPartnerAmount(
        partner,
        TNN_PARTNER_AMOUNT_FIELDS.share,
        data.options,
      );

  const totalEvents = Number(partner.total_events ?? 0);
  const campaignGoal =
    data.numberOfRuns != null ? data.numberOfRuns * totalEvents : null;
  const runsToDate =
    partner.runs_to_date != null ? Number(partner.runs_to_date) : 0;
  const rawPercent =
    campaignGoal && campaignGoal > 0 ? (runsToDate / campaignGoal) * 100 : 0;
  const percentComplete = `${Math.min(100, rawPercent).toFixed(1)}%`;

  return (
    <Box style={{...style, paddingBottom: 8}}>
      <PartnerAmountGrid variant='row' hasRunsMetrics={data.numberOfRuns != null}>
        <Stack sx={{minWidth: 0}}>
          <Typography noWrap title={name} sx={{minWidth: 0}}>
            {name || '—'}
          </Typography>
          {children.length > 0 ? (
            <Link
              component='button'
              type='button'
              underline='none'
              aria-expanded={isExpanded}
              onClick={() => data.onToggleDistrict(partner.id)}
              title={tnnDistrictPartnerConfig.districtCoverageHint(
                children.length,
              )}
              sx={{
                alignSelf: 'flex-start',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.25,
                mt: 0.25,
                fontSize: 11,
                lineHeight: 1.4,
                color: 'text.secondary',
                '&:hover': {color: 'primary.main'},
              }}>
              {isExpanded ? (
                <ExpandLess sx={{fontSize: 14}} />
              ) : (
                <ExpandMore sx={{fontSize: 14}} />
              )}
              {tnnDistrictPartnerConfig.districtChildCount(children.length)}
            </Link>
          ) : null}
        </Stack>
        {data.editable ? (
          <TextField
            size='small'
            type='number'
            value={price ?? ''}
            onChange={event =>
              data.onPriceChange?.(Number(partner.id), event.target.value)
            }
            inputProps={{
              min: 0,
              step: 0.01,
              'aria-label': `Partner Price for ${name || partner.id}`,
            }}
            sx={{
              width: 100,
              '& .MuiInputBase-root': {height: 36},
            }}
          />
        ) : (
          <Typography>
            {formatTnnCurrency(
              price,
              data.emptyValue,
              data.currencyMinimumFractionDigits,
            )}
          </Typography>
        )}
        <Typography noWrap>
          {formatTnnCurrency(
            share,
            data.emptyValue,
            data.currencyMinimumFractionDigits,
          )}
        </Typography>
        {data.numberOfRuns != null ? (
          <>
            <Typography noWrap>
              {campaignGoal != null ? campaignGoal : data.emptyValue}
            </Typography>
            <Typography noWrap>{runsToDate}</Typography>
            <Typography noWrap>{percentComplete}</Typography>
          </>
        ) : null}
      </PartnerAmountGrid>
      <Divider sx={{borderColor: 'divider'}} />
    </Box>
  );
};

export type TnnPartnerAmountTableProps = {
  title: string;
  partners: ITnnPartnerAmountPartner[];
  editable?: boolean;
  onPriceChange?: (companyId: number, value: string) => void;
  listHeight?: number;
  emptyLabel?: string;
  emptyValue?: string;
  preferPivot?: boolean;
  calculateShareWhenMissing?: boolean;
  currencyMinimumFractionDigits?: number;
  numberOfRuns?: number;
};

export const TnnPartnerAmountTotals = ({
  price,
  share,
  label = 'Total',
  boxed = false,
  emptyValue = '$ 0.00',
  currencyMinimumFractionDigits = 2,
  numberOfRuns,
}: {
  price: number | string | null;
  share: number | string | null;
  label?: string;
  boxed?: boolean;
  emptyValue?: string;
  currencyMinimumFractionDigits?: number;
  numberOfRuns?: number;
}) => {
  const hasRunsMetrics = numberOfRuns != null;

  return (
    <Stack
      sx={
        boxed
          ? {
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 3.5,
              bgcolor: 'background.default',
            }
          : undefined
      }>
      <PartnerAmountGrid variant='total' hasRunsMetrics={hasRunsMetrics}>
        <Typography>{label}</Typography>
        <Typography>
          {formatTnnCurrency(price, emptyValue, currencyMinimumFractionDigits)}
        </Typography>
        <Typography>
          {formatTnnCurrency(share, emptyValue, currencyMinimumFractionDigits)}
        </Typography>
      </PartnerAmountGrid>
    </Stack>
  );
};

export const TnnPartnerAmountTable = ({
  title,
  partners,
  editable = false,
  onPriceChange,
  listHeight,
  emptyLabel = 'No partners selected.',
  emptyValue = '—',
  preferPivot = false,
  calculateShareWhenMissing = true,
  currencyMinimumFractionDigits = 2,
  numberOfRuns,
}: TnnPartnerAmountTableProps) => {
  const [expandedDistrictIds, setExpandedDistrictIds] = useState<
    (number | string)[]
  >([]);

  const onToggleDistrict = useCallback((districtId: number | string) => {
    setExpandedDistrictIds(prev =>
      prev.includes(districtId)
        ? prev.filter(id => id !== districtId)
        : [...prev, districtId],
    );
  }, []);

  const rows = useMemo(() => {
    const partnerRows: PartnerAmountRowItem[] = [];

    partners.forEach(partner => {
      partnerRows.push({kind: 'partner', partner});

      if (expandedDistrictIds.includes(partner.id)) {
        const children = getInheritedChildren(partner);
        children.forEach((child, childIndex) => {
          partnerRows.push({
            kind: 'child',
            child,
            districtId: partner.id,
            isLastChild: childIndex === children.length - 1,
          });
        });
      }
    });

    return partnerRows;
  }, [partners, expandedDistrictIds]);

  const height = listHeight ?? Math.min(320, Math.max(64, rows.length * 64));
  const options = {preferPivot, calculateShareWhenMissing};
  const totalPrice = sumPartnerAmount(
    partners,
    TNN_PARTNER_AMOUNT_FIELDS.price,
    options,
  );
  const totalShare = sumPartnerShare(partners, options);

  return (
    <Stack
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3.5,
        bgcolor: 'background.default',
        overflow: 'hidden',
      }}>
      <PartnerAmountGrid hasRunsMetrics={numberOfRuns != null}>
        <Typography noWrap sx={{color: 'text.secondary', fontSize: 14}}>
          {title} ({partners.length})
        </Typography>
        <Typography sx={{color: 'text.secondary', fontSize: 14}}>
          Partner Price
        </Typography>
        <Typography sx={{color: 'text.secondary', fontSize: 14}}>
          Partner Share
        </Typography>
        {numberOfRuns != null ? (
          <>
            <Typography sx={{color: 'text.secondary', fontSize: 14}}>
              Campaign Goals
            </Typography>
            <Typography sx={{color: 'text.secondary', fontSize: 14}}>
              Runs To Date
            </Typography>
            <Typography sx={{color: 'text.secondary', fontSize: 14}}>
              Campaign % Complete
            </Typography>
          </>
        ) : null}
      </PartnerAmountGrid>
      <Divider sx={{borderColor: 'divider'}} />
      {partners.length > 0 ? (
        <Lists
          height={height}
          itemCount={rows.length}
          itemSize={64}
          itemKey={(index: number) => {
            const row = rows[index];
            if (!row) return index;
            return row.kind === 'child'
              ? `child-${row.districtId}-${row.child.id}`
              : `partner-${row.partner.id}`;
          }}
          width='100%'
          className='scrollbar'
          itemData={{
            rows,
            expandedDistrictIds,
            onToggleDistrict,
            editable,
            onPriceChange,
            emptyValue,
            options,
            calculateShareWhenMissing,
            currencyMinimumFractionDigits,
            numberOfRuns,
          }}>
          {PartnerAmountRow}
        </Lists>
      ) : (
        <Typography sx={{px: 3, py: 2}} color='text.secondary'>
          {emptyLabel}
        </Typography>
      )}
      <TnnPartnerAmountTotals
        price={totalPrice}
        share={totalShare}
        emptyValue={emptyValue}
        currencyMinimumFractionDigits={currencyMinimumFractionDigits}
        numberOfRuns={numberOfRuns}
      />
    </Stack>
  );
};
